import { useEffect, useMemo, useRef, type RefObject } from 'react';
import type { Folder } from '@/api';
import type { MenuAnchor, MenuNode, MenuVariant } from '@/features/context-menu';
import { DIRECTION_LOCK_PX, LONG_PRESS_MS } from '@/hooks/gestureSlop';

/**
 * The folder menu: ONE list of verbs, whichever way it was asked for.
 *
 * The tree's ⋯ button, a right-click on the row, `Shift+F10` / the ContextMenu
 * key on a focused row, and a long-press in the handheld drawer all open the
 * same `Menu` (features/context-menu, the message list's own APG menu) with
 * the items built here. The mail list's header button asks for the `content`
 * subset of the same list, so "Mark all as read" means one thing everywhere.
 *
 * Items that cannot work are **absent, not disabled**:
 *
 * - A folder that holds no mail of its own (`\Noselect`) gets no Open, Refresh,
 *   Mark all as read or Empty — there is no list to open and nothing to mark.
 * - A folder the app resolves by role (Inbox, Drafts, Sent, Archive, Junk,
 *   Trash) or one holding such a folder gets no Rename or Delete: the backend
 *   refuses both with `409 role_folder`, so offering them could only fail.
 * - There is no Move: `PATCH /folders/{id}` renames the last segment only and
 *   keeps the parent, and nothing else in the API re-parents a folder.
 * - Open in new tab is a desktop affordance, as "Open in new window" is on the
 *   message menu: in the handheld sheet a second tab of an installed app is a
 *   second copy of the same screen.
 */

export interface FolderMenuHandlers {
  openInNewTab: (folder: Folder) => void;
  refresh: (folder: Folder) => void;
  markRead: (folder: Folder) => void;
  /** Opens the two-step Empty dialog (FolderDialog). */
  empty: (folder: Folder) => void;
  /** Opens the New folder dialog with `folder` as the parent. */
  createInside: (folder: Folder) => void;
  rename: (folder: Folder) => void;
  remove: (folder: Folder) => void;
}

export interface FolderMenuOptions {
  /** `content` is the mail list header's subset: Mark all as read and Empty. */
  scope?: 'full' | 'content';
  variant?: MenuVariant;
  /** False for a role folder, or one holding a role folder (`protectedBy`). */
  canModify: boolean;
}

/** Groups joined with separators, with no separator around an empty group. */
function joinGroups(groups: MenuNode[][]): MenuNode[] {
  const out: MenuNode[] = [];
  groups
    .filter((group) => group.length > 0)
    .forEach((group, index) => {
      if (index > 0) out.push({ kind: 'separator', id: `sep-${index}` });
      out.push(...group);
    });
  return out;
}

export function folderMenuItems(
  folder: Folder,
  handlers: FolderMenuHandlers,
  { scope = 'full', variant = 'popover', canModify }: FolderMenuOptions,
): MenuNode[] {
  const holdsMail = folder.selectable;
  // Emptying Trash or Junk deletes for good; everywhere else it moves to
  // Trash. The dialog says which with the server's count -- this only paints
  // the permanent one in the danger colour so it does not read as routine.
  const permanent = folder.role === 'trash' || folder.role === 'junk';

  const content: MenuNode[] = holdsMail
    ? [
        {
          kind: 'item',
          id: 'mark-read',
          label: 'Mark all as read',
          onSelect: () => handlers.markRead(folder),
        },
        {
          kind: 'item',
          id: 'empty',
          label: 'Empty folder…',
          danger: permanent,
          onSelect: () => handlers.empty(folder),
        },
      ]
    : [];
  if (scope === 'content') return content;

  const view: MenuNode[] = holdsMail
    ? [
        ...(variant === 'popover'
          ? [
              {
                kind: 'item' as const,
                id: 'open-tab',
                label: 'Open in new tab',
                onSelect: () => handlers.openInNewTab(folder),
              },
            ]
          : []),
        { kind: 'item', id: 'refresh', label: 'Refresh', onSelect: () => handlers.refresh(folder) },
      ]
    : [];

  const structure: MenuNode[] = [
    {
      kind: 'item',
      id: 'new-subfolder',
      label: 'New subfolder…',
      onSelect: () => handlers.createInside(folder),
    },
    ...(canModify
      ? [
          { kind: 'item' as const, id: 'rename', label: 'Rename…', onSelect: () => handlers.rename(folder) },
          {
            kind: 'item' as const,
            id: 'delete',
            label: 'Delete…',
            danger: true,
            onSelect: () => handlers.remove(folder),
          },
        ]
      : []),
  ];

  return joinGroups([view, content, structure]);
}

/** What a folder's route is, for a new tab: the account travels with it (ADR 0012). */
export function folderHref(folder: Folder): string {
  return `/mail/${encodeURIComponent(folder.id)}?account=${encodeURIComponent(folder.accountId)}`;
}

/** Opens `href` in a new tab of this app, with no handle back to this one. */
export function openInNewTab(href: string): void {
  window.open(href, '_blank', 'noopener');
}

/**
 * A menu button that toggles: a press on it while its menu is open closes the
 * menu and must not open it again.
 *
 * The menu's own outside-press rule closes it on the button's `pointerdown`,
 * and the browser runs React's re-render in the microtask checkpoint between
 * that listener and the button's own, so by the time the button hears its
 * press the menu already reads as closed. What survives is the EVENT: `close`
 * notes the event that caused it (`window.event`), and the button ignores the
 * click that follows a press which was that same event.
 */
export function useMenuButtonToggle(): {
  /** Call from the menu's `onClose`. */
  noteClose: () => void;
  onPointerDown: (event: { nativeEvent: Event }) => void;
  /** True for the click that belongs to a press that just closed the menu. */
  consumeClick: () => boolean;
} {
  const closedBy = useRef<Event | undefined>(undefined);
  const pressClosed = useRef(false);
  // Stable: `noteClose` sits in the menu's `onClose`, and a new function per
  // render would re-bind the menu's document listeners on every render.
  return useMemo(
    () => ({
      noteClose: () => {
        closedBy.current = typeof window === 'undefined' ? undefined : window.event;
      },
      onPointerDown: (event: { nativeEvent: Event }) => {
        pressClosed.current = closedBy.current !== undefined && closedBy.current === event.nativeEvent;
      },
      consumeClick: () => {
        const consumed = pressClosed.current;
        pressClosed.current = false;
        return consumed;
      },
    }),
    [],
  );
}

export type RowMenuSource = 'pointer' | 'keyboard' | 'touch';

/**
 * The three ways into a row's menu that are not its ⋯ button, bound to the
 * row element:
 *
 * - **Right-click** opens at the pointer. The native menu is suppressed on the
 *   row only; everywhere else in the sidebar it is the browser's.
 * - **Shift+F10 / the ContextMenu key** on anything focused in the row opens
 *   under the row, and focus returns to what had it.
 * - **Long-press** (touch): `LONG_PRESS_MS` with no more than
 *   `DIRECTION_LOCK_PX` of drift — the same slop swipe and pull-to-refresh
 *   decide at, so a press that starts to scroll the drawer is a scroll, and a
 *   `pointercancel` (the browser took the touch for scrolling) drops it. The
 *   click that the same touch produces on release is cancelled, so a
 *   long-press never also navigates, nor lands on the sheet's scrim and shuts
 *   it; a plain tap still navigates.
 *
 * Chrome on Android also fires `contextmenu` for a long touch; that one is
 * only kept from showing the native menu, because the timer has it.
 *
 * Bound imperatively, as `Sheet` binds its keys: the row is not itself a
 * widget (its buttons are), and these are listeners on a container.
 */
export function useRowMenuTriggers(
  rowRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  open: (anchor: MenuAnchor, returnFocusTo: HTMLElement | null, source: RowMenuSource) => void,
): void {
  // Through a ref, so an inline `open` does not re-bind every listener on
  // every render of every row.
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row || !enabled) return undefined;

    let press: { timer: ReturnType<typeof setTimeout>; x: number; y: number } | null = null;
    let swallowClick = false;
    let lastPointer = 'mouse';

    const focusTarget = (): HTMLElement | null =>
      row.querySelector<HTMLElement>('.folder-tree__main') ?? row;
    const rowBox = (): DOMRect =>
      (row.querySelector('.folder-tree__row') ?? row).getBoundingClientRect();
    const cancel = (): void => {
      if (press) clearTimeout(press.timer);
      press = null;
    };

    const onContextMenu = (event: MouseEvent): void => {
      event.preventDefault();
      if (lastPointer === 'touch') return;
      openRef.current({ x: event.clientX, y: event.clientY }, focusTarget(), 'pointer');
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!((event.key === 'F10' && event.shiftKey) || event.key === 'ContextMenu')) return;
      event.preventDefault();
      event.stopPropagation();
      const focused = document.activeElement;
      openRef.current(
        rowBox(),
        focused instanceof HTMLElement && row.contains(focused) ? focused : focusTarget(),
        'keyboard',
      );
    };
    const onPointerDown = (event: PointerEvent): void => {
      lastPointer = event.pointerType;
      swallowClick = false;
      if (event.pointerType !== 'touch') return;
      cancel();
      press = {
        x: event.clientX,
        y: event.clientY,
        timer: setTimeout(() => {
          press = null;
          swallowClick = true;
          openRef.current(rowBox(), focusTarget(), 'touch');
        }, LONG_PRESS_MS),
      };
    };
    const onPointerMove = (event: PointerEvent): void => {
      if (!press) return;
      if (
        Math.abs(event.clientX - press.x) > DIRECTION_LOCK_PX ||
        Math.abs(event.clientY - press.y) > DIRECTION_LOCK_PX
      ) {
        cancel();
      }
    };
    const onClick = (event: MouseEvent): void => {
      if (!swallowClick) return;
      swallowClick = false;
      event.preventDefault();
      event.stopPropagation();
    };
    // The click a touch produces is aimed at whatever is under the finger when
    // it LIFTS -- after a long-press that is the sheet's scrim, which would
    // close the menu the instant it opened. Cancelling the touchend cancels
    // that click at the source. Touch events stay targeted at the row the
    // touch began on, so this still hears the release with the sheet on top.
    const onTouchEnd = (event: TouchEvent): void => {
      if (!swallowClick) return;
      swallowClick = false;
      if (event.cancelable) event.preventDefault();
    };

    row.addEventListener('contextmenu', onContextMenu);
    row.addEventListener('keydown', onKeyDown);
    row.addEventListener('pointerdown', onPointerDown);
    row.addEventListener('pointermove', onPointerMove);
    row.addEventListener('pointerup', cancel);
    row.addEventListener('pointercancel', cancel);
    // Capture, so the swallowed click never reaches the folder's button.
    row.addEventListener('click', onClick, true);
    // Not passive: it has to be able to cancel the click (see onTouchEnd).
    row.addEventListener('touchend', onTouchEnd, { passive: false });
    return () => {
      cancel();
      row.removeEventListener('contextmenu', onContextMenu);
      row.removeEventListener('keydown', onKeyDown);
      row.removeEventListener('pointerdown', onPointerDown);
      row.removeEventListener('pointermove', onPointerMove);
      row.removeEventListener('pointerup', cancel);
      row.removeEventListener('pointercancel', cancel);
      row.removeEventListener('click', onClick, true);
      row.removeEventListener('touchend', onTouchEnd);
    };
  }, [enabled, rowRef]);
}
